/**
 * Utility Functions for Query Parser
 */

"use strict";

import { escapeRegExp } from '../../utils/regex-utils.js';

/**
 * Process wildcards in pattern: ?? = exactly 2 chars, * = any number
 * IMPORTANT: single ? wildcard is disabled to avoid mangling system regex constructs like (?<!
 * @param {string} pattern - Pattern string (may contain escaped special chars)
 * @returns {string} - Pattern with wildcards replaced
 */
export function processWildcards(pattern) {
    if (!pattern || typeof pattern !== 'string') return pattern;
    const reAllLetters = window.reAllLetters || 'A-Za-zА-Яа-яЁё';
    let result = pattern;
    // Replace ?? first (before single ?) - exactly 2 characters
    result = result.replace(/\?\?/g, `[${reAllLetters}]{2}`);
    // Replace escaped ?* (already escaped as \? or \*) - these become single char wildcards
    result = result.replace(/\\([\?\*])/g, `[${reAllLetters}]$1`);
    // Replace unescaped * (for word patterns) - any number of characters
    result = result.replace(/([^\\])\*/g, `$1[${reAllLetters}]*`);
    // Handle * at the start of string
    if (result.startsWith('*')) {
        result = `[${reAllLetters}]*` + result.substring(1);
    }
    // Single ? wildcard is intentionally disabled here because it conflicts with (? constructs.
    // If needed, it must be handled BEFORE combining with regex syntax.
    return result;
}

/**
 * Preserve wildcards and path separators when escaping category patterns
 * @param {string} str - String to escape
 * @returns {string} - Escaped string
 */
export function escapeForCategory(str) {
    if (typeof str !== 'string') return '';
    const STAR = '__CAT_STAR__';
    const SLASH = '__CAT_SLASH__';
    const SEMI = '__CAT_SEMI__';
    let s = str.replace(/\*/g, STAR).replace(/[\/]/g, SLASH).replace(/;/g, SEMI);
    s = s.replace(/[.+?^${}()|[\]\\]/g, '\\$&');
    s = s.replace(new RegExp(STAR, 'g'), '*').replace(new RegExp(SLASH, 'g'), '/').replace(new RegExp(SEMI, 'g'), ';');
    return s;
}

/**
 * Convert Arabic number (1-12) to Roman numeral
 * @param {number} num - Arabic number (1-12)
 * @returns {string|null} Roman numeral or null if out of range
 */
export function arabicToRoman(num) {
    const romanMap = {
        1: 'I', 2: 'II', 3: 'III', 4: 'IV', 5: 'V',
        6: 'VI', 7: 'VII', 8: 'VIII', 9: 'IX',
        10: 'X', 11: 'XI', 12: 'XII'
    };
    return romanMap[num] || null;
}

/**
 * Check if a string is a standalone number from 1 to 12
 * @param {string} str - String to check
 * @returns {number|null} Number if valid (1-12), null otherwise
 */
export function isStandaloneNumber(str) {
    const num = parseInt(str, 10);
    if (isNaN(num)) return null;
    if (num >= 1 && num <= 12) return num;
    return null;
}
